import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { useAddToCartMutation } from 'Services/cartApi';
import { useLazyGetProductQuery } from 'Services/productDetailApi';
import {
    handleAddToCartDatalayerPush,
    handleChangeSizeVariantDatalayerPush,
} from 'Utils/dataLayerPushService';
import { BreakPointHooks } from 'Hooks/BreakPointHooks';
import useInView from 'Hooks/useInView';

import { IProductItemViewModel, IProductPageViewModel } from 'Types/Litium';
import CtaButton from 'Components/CtaButton';
import QuantityInputField from 'Components/Forms/QuantityInputField';
import Markup from 'Components/Markup';
import WishListButton from 'Components/WishListButton';

import CrossIcon from 'Icons/closeThin.svg?react';

interface ProductInfoProps {
    productItem: IProductItemViewModel;
    colorText: IProductPageViewModel['colorText'];
    productFilters: IProductPageViewModel['productFilters'];
    sizeText: IProductPageViewModel['sizeText'];
    whatsMySizePage: IProductPageViewModel['whatsMySizePage'];
    translations: IProductPageViewModel['translations'];
    productWebText: IProductPageViewModel['productWebText'];
    productWebRichText: IProductPageViewModel['productWebRichText'];
    showQuantityField: IProductItemViewModel['showQuantityField'];
    valueText: IProductPageViewModel['valueText'];
    isOnWishList: IProductItemViewModel['isOnWishList'];
    enabledWishlist: IProductItemViewModel['enabledWishlist'];
    preSelected: boolean;
}
const ProductInfo = ({
    productFilters,
    productItem,
    colorText,
    sizeText,
    whatsMySizePage,
    translations,
    productWebText,
    productWebRichText,
    showQuantityField,
    valueText,
    isOnWishList,
    enabledWishlist,
    preSelected,
}: ProductInfoProps) => {
    const sizeOrValueList = [...productFilters.values, ...productFilters.sizes];
    const [
        addToCart,
        {
            isLoading: isAddToCartLoading,
            isError: isErrorAddToCart,
            error: errorAddToCart,
        },
    ] = useAddToCartMutation();
    const [getProduct, result] = useLazyGetProductQuery();
    const {
        isError: isErrorGetProduct,
        error: errorGetProduct,
        isFetching: getProductIsFetching,
        isLoading: isGetProductLoading,
    } = result;

    const { isPurchasable } = productItem;
    const isOneSizeAndIsEnabled =
        productFilters.sizes.length === 1 && productFilters.sizes[0].enabled;
    const hasSizes = productFilters.sizes.length >= 1;

    const [selectedVariant, setSelectedVariant] = useState(
        preSelected || isOneSizeAndIsEnabled
    );

    const ctaBtnDisabled =
        isAddToCartLoading ||
        getProductIsFetching ||
        !selectedVariant ||
        !isPurchasable;

    const sizeBtnWidth =
        (sizeOrValueList.length > 0 &&
            sizeOrValueList.reduce((longest, current) =>
                current && current.title.length > longest.title.length
                    ? current
                    : longest
            ).title.length) ||
        0;

    const [itemQuantity, setItemQuantity] = useState(1);
    const [sizeAnimation, setSizeAnimation] = useState(false);

    const { uspText1, uspText2, uspText3 } = translations;
    const handleChangeSizeVariant = (id: string, isActive: boolean) => {
        setSelectedVariant(true);
        setSizeAnimation(false);
        if (!isActive) {
            getProduct({ id, slideIndex: 0 });
            handleChangeSizeVariantDatalayerPush(productItem.trackingData);
        }
        setItemQuantity(1);
    };

    const { useGreater } = BreakPointHooks();
    const isGreater = useGreater('lg');

    const buttonOuterContainerRef = useRef<HTMLDivElement>();
    const stockStatusRef = useRef<HTMLDivElement>();
    const ANIMATIONTIMEOUT: number = 4000;

    const {
        inView: buttonOuterContainerInView,
        boundingClientRect: buttonOuterContainerBoundingClientRect,
    } = useInView(buttonOuterContainerRef);

    const currentIsFixed =
        !buttonOuterContainerInView &&
        buttonOuterContainerBoundingClientRect?.top >= 0;

    const handleChangeColorVariant = (id: string) => {
        getProduct({ id, slideIndex: 0 });
        setItemQuantity(1);
    };

    const handleAddToCart = () => {
        if (!selectedVariant) {
            setSizeAnimation(true);
        }
        if (!selectedVariant && currentIsFixed) {
            stockStatusRef?.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
        } else if (selectedVariant && isPurchasable) {
            handleAddToCartDatalayerPush(productItem.trackingData);

            if (showQuantityField) {
                addToCart({
                    quantity: +itemQuantity,
                    articleNumber: productItem.id,
                });
            } else {
                addToCart({
                    quantity: 1,
                    articleNumber: productItem.id,
                });
            }
        }
    };

    const handleChangeQuantity = (
        currentQuantity: number,
        decrement: boolean
    ) => {
        if (decrement && currentQuantity <= 1) {
            return;
        }
        if (decrement) {
            setItemQuantity(currentQuantity - 1);
        } else {
            setItemQuantity(currentQuantity + 1);
        }
    };

    const campaignPrice = productItem?.priceVM?.campaignPrice;
    const clubPrice = productItem?.priceVM?.clubPrice;
    const basePrice = productItem?.priceVM?.basePrice;
    const buyXPrice = productItem?.priceVM?.buyXCampaigPrice;

    const hasCampaignOrClubPrices =
        clubPrice?.formattedPrice.length > 0 ||
        campaignPrice?.formattedPrice.length > 0;

    useEffect(() => {
        if (preSelected) {
            setSelectedVariant(true);
        }
    }, [preSelected]);

    useEffect(() => {
        if (isErrorAddToCart && 'error' in errorAddToCart) {
            const errMsg = errorAddToCart.error;
            toast.error(errMsg);
        }
    }, [isErrorAddToCart, errorAddToCart]);
    useEffect(() => {
        if (isErrorGetProduct && 'error' in errorGetProduct) {
            const errMsg = errorGetProduct.error;
            toast.error(errMsg);
        }
    }, [errorGetProduct, isErrorGetProduct]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        if (sizeAnimation) {
            timeoutId = setTimeout(() => {
                setSizeAnimation(false);
            }, ANIMATIONTIMEOUT);
        }
        return () => clearTimeout(timeoutId);
    }, [sizeAnimation]);

    return (
        <div className="flex flex-col gap-6">
            <div className="first align-center flex justify-between">
                <h1 className="font-montserrat leading-[1.125] text-black -:text-2xl -:lg:text-3xl">
                    {productItem.name}
                </h1>
                <div className="flex items-start justify-center">
                    {enabledWishlist && (
                        <WishListButton
                            enabledWishlist={enabledWishlist}
                            id={productItem.id}
                            isOnWishList={isOnWishList}
                            width="34px"
                            height="34px"
                            stroke="1"
                        />
                    )}
                </div>
            </div>
            {productWebText && <p>{productWebText}</p>}

            {productWebRichText && (
                <Markup
                    html={productWebRichText.value}
                    cssClass="prose-block w-full mb-0"
                />
            )}

            <ul className="price-container flex flex-col">
                {clubPrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    clubPrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className="font-roboto text-2xl font-normal leading-normal text-[var(--hex-text-color)]"
                    >
                        <span className="mr-1">{clubPrice?.name}</span>
                        {clubPrice?.formattedPrice}
                    </li>
                )}
                {campaignPrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    campaignPrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className="font-roboto text-2xl font-normal leading-normal text-[var(--hex-text-color)]"
                    >
                        <span className="mr-1">{campaignPrice?.name}</span>
                        {campaignPrice?.formattedPrice}
                    </li>
                )}
                {buyXPrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    buyXPrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className="font-roboto text-2xl font-normal leading-normal text-[var(--hex-text-color)]"
                    >
                        <span className="mr-1">{buyXPrice?.name}</span>
                        {buyXPrice?.formattedPrice}
                    </li>
                )}
                {basePrice?.formattedPrice.length > 0 && (
                    <li
                        style={
                            {
                                '--hex-text-color':
                                    basePrice?.priceListColorCode,
                            } as React.CSSProperties
                        }
                        className={`font-roboto ${
                            hasCampaignOrClubPrices ? 'text-lg' : 'text-xl'
                        } leading-normal text-[var(--hex-text-color)]`}
                    >
                        <span className="mr-1">{basePrice?.name}</span>
                        {basePrice?.formattedPrice}
                    </li>
                )}
            </ul>
            <div className="flex flex-col">
                {hasSizes && (
                    <div className="mb-2.5 flex items-center">
                        <p
                            id="colorText"
                            className="font-roboto text-base font-medium leading-normal text-black"
                        >
                            {colorText}
                        </p>
                        <p className="font-roboto text-base capitalize text-black before:mx-3 before:border before:border-emperor">
                            {
                                productFilters.colors.find(
                                    color => color.isActive
                                )?.title
                            }
                        </p>
                        <p className="ml-1 font-roboto text-base text-black">
                            {
                                productFilters.colors.find(
                                    color => color.isActive
                                )?.colorCode
                            }
                        </p>
                    </div>
                )}
                <ul className="flex w-full flex-wrap gap-3">
                    {productFilters.colors.map((color, index) => (
                        <li
                            className="relative flex"
                            key={index}
                            style={
                                {
                                    '--hex-bg-color': color.colorHex,
                                } as React.CSSProperties
                            }
                        >
                            <button
                                aria-describedby="colorText"
                                aria-label={color.title}
                                type="button"
                                disabled={getProductIsFetching}
                                title={color.title}
                                onClick={() =>
                                    handleChangeColorVariant(color.id)
                                }
                                className={` flex h-9 w-9 cursor-pointer rounded-full border border-solid border-gray-300 bg-[var(--hex-bg-color)] focus-visible:outline-mabile 
                                ${
                                    color.isActive
                                        ? 'pointer-events-none cursor-none outline outline-1 outline-offset-2 outline-internalGray'
                                        : ''
                                }`}
                            />

                            {!color.enabled && (
                                <div className="pointer-events-none absolute flex h-full w-full items-center justify-center">
                                    <CrossIcon
                                        className={`pointer-events-none top-0 flex h-6 w-6 ${
                                            color.colorHex.includes('#f')
                                                ? 'text-grey-100'
                                                : 'text-white'
                                        }`}
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="flex flex-wrap">
                <div
                    className={`relative mb-2 flex w-full flex-initial flex-row flex-wrap justify-between lg:mb-1 
                ${
                    sizeAnimation
                        ? 'animate after:visible after:absolute after:left-[-8px] after:top-[-4px] after:z-[1] after:flex after:h-[calc(100%_+_10px)] after:w-[calc(100%_+_20px)] after:animate-pulse-two-sec after:rounded-sm after:border after:border-solid after:border-cadillac after:opacity-0'
                        : 'after:invisible'
                } `}
                >
                    <p
                        id={hasSizes ? 'sizeText' : 'valueText'}
                        className="z-[2] font-roboto font-medium text-black"
                    >
                        {hasSizes ? sizeText : valueText}
                    </p>

                    <div className="z-[2] flex w-full flex-wrap items-center justify-between gap-1">
                        <ul className="flex flex-wrap gap-2">
                            {sizeOrValueList?.length > 0 &&
                                sizeOrValueList.map((size, index) => (
                                    <li
                                        key={index}
                                        style={
                                            {
                                                '--btn-size': `calc(${sizeBtnWidth}ch + 36px)`,
                                            } as React.CSSProperties
                                        }
                                        className="flex w-[var(--btn-size)] items-center"
                                    >
                                        <button
                                            aria-describedby={
                                                hasSizes
                                                    ? 'sizeText'
                                                    : 'valueText'
                                            }
                                            className={`size-selector flex w-full cursor-pointer border border-solid border-gray-300 font-roboto text-base font-semibold leading-normal${
                                                size.isActive &&
                                                selectedVariant &&
                                                !isGetProductLoading
                                                    ? ' pointer-events-none bg-black text-white'
                                                    : ' bg-white text-black'
                                            }${
                                                size.enabled
                                                    ? ' in-stock'
                                                    : ' out-of-stock'
                                            }`}
                                            onClick={() =>
                                                handleChangeSizeVariant(
                                                    size.id,
                                                    size.isActive
                                                )
                                            }
                                            disabled={getProductIsFetching}
                                        >
                                            <span
                                                className={`relative mx-4 my-2 flex w-full items-center justify-center font-roboto text-base font-bold leading-normal ${
                                                    size.isActive &&
                                                    selectedVariant &&
                                                    !isGetProductLoading
                                                        ? ' text-white'
                                                        : 'text-black'
                                                } ${
                                                    !size.enabled &&
                                                    'before:absolute before:top-2.5 before:h-[2px] before:w-[100%] before:bg-black'
                                                }`}
                                            >
                                                {size.title}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                        </ul>
                        {whatsMySizePage && hasSizes && (
                            <a
                                target="_blank"
                                rel="noreferrer"
                                className="hidden text-nowrap font-roboto text-base leading-normal text-black underline lg:block"
                                href={whatsMySizePage.href}
                            >
                                {whatsMySizePage.text}
                            </a>
                        )}
                    </div>
                </div>
            </div>
            {whatsMySizePage && (
                <a
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-[1_0_auto] justify-end font-roboto text-base leading-normal text-black underline lg:hidden"
                    href={whatsMySizePage.href}
                >
                    {whatsMySizePage.text}
                </a>
            )}
            <div className="flex w-full flex-wrap gap-2">
                {showQuantityField && (
                    <div className="flex flex-col gap-1">
                        <p>{translations.quantity}</p>
                        <QuantityInputField
                            value={itemQuantity}
                            minusAction={() =>
                                handleChangeQuantity(itemQuantity, true)
                            }
                            plusAction={() =>
                                handleChangeQuantity(itemQuantity, false)
                            }
                            minusDisabled={
                                itemQuantity === 1 ||
                                getProductIsFetching ||
                                !selectedVariant
                            }
                            plusDisabled={
                                getProductIsFetching || !selectedVariant
                            }
                            translations={{
                                decrementButton:
                                    translations.checkoutDecrementButton,
                                incrementButton:
                                    translations.checkoutIncreaseButton,
                                quantityLabel: translations.quantity,
                            }}
                        />
                    </div>
                )}
                <div
                    ref={buttonOuterContainerRef}
                    className="relative flex h-[50px] w-full flex-col gap-8"
                >
                    <div
                        className={`flex h-[50px] flex-col transition-[padding] duration-layout lg:relative lg:w-full lg:p-0 ${
                            currentIsFixed && !isGreater
                                ? 'fixed bottom-0 left-0 z-[2] h-fit w-[calc((100vw-(100vw-100%))-var(--scrollbar-width))] px-[var(--padding-x-mobile)]'
                                : 'absolute -left-[calc((var(--pdp-aside-padding-x-mobile)_+_var(--padding-x-mobile)))] w-[calc((100vw-(100vw-100%))+_var(--pdp-aside-padding-x-mobile)_+_var(--padding-x-mobile)+_var(--pdp-aside-padding-x-mobile)_+_var(--padding-x-mobile))] px-[calc(var(--pdp-aside-padding-x-mobile)_+_var(--padding-x-mobile))] lg:left-0'
                        }`}
                    >
                        <div
                            className={`bg-white ${
                                currentIsFixed && !isGreater ? 'py-1' : ''
                            }`}
                        >
                            <CtaButton
                                className="add-to-cart-button flex w-full flex-auto items-center justify-center p-2.5 font-montserrat text-xl font-medium transition-colors duration-layout"
                                onClick={handleAddToCart}
                                loading={ctaBtnDisabled}
                            >
                                {translations.buyButtonText}
                            </CtaButton>
                        </div>
                    </div>
                </div>

                {productItem.stockStateText && (
                    <div className="stock-status mt-3 flex">
                        <p
                            ref={stockStatusRef}
                            style={
                                {
                                    '--dot-color': productItem.stockStatusHex,
                                } as React.CSSProperties
                            }
                            className={`flex transition-all duration-layout ease-in ${
                                selectedVariant && !getProductIsFetching
                                    ? 'visible opacity-100'
                                    : 'invisible opacity-0'
                            } items-center font-roboto text-sm before:mr-1.5 before:h-4.5 before:w-4.5 before:rounded-full before:bg-[var(--dot-color)] before:content-['']`}
                        >
                            {productItem.stockStateText}
                        </p>
                    </div>
                )}
            </div>
            <ul className="mb-4 flex flex-col">
                <li className="ml-4.5 list-disc font-roboto text-base">
                    {uspText1}
                </li>
                <li className="ml-4.5 list-disc font-roboto text-base">
                    {uspText2}
                </li>
                <li className="ml-4.5 list-disc font-roboto text-base">
                    {uspText3}
                </li>
            </ul>
        </div>
    );
};

export default ProductInfo;
